import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import config from "../../config/config";
import { getToken } from "../../utils/localStorageMethods";
import i18n from "./i18n";
import { translateManyClient } from "../../utils/ItClient";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", true);
    }
    return headers;
  },
});

const baseQueryWithRetry = retry(baseQuery, {
  maxRetries: 1,
  retryCondition: (error, _args, { attempt }) => {
    // sirf error hone par retry
    return !!error && attempt <= 1;
  },
});

export const configurationSlice = createApi({
  reducerPath: "configurationSlice",
  baseQuery: baseQueryWithRetry,
  tagTypes: ["configurations"],
  endpoints: (builder) => ({
    // GET All Records
    getData: builder.query({
      query: ({ page, limit, search, endPoint }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);

        const queryString = params.toString();
        return queryString ? `${endPoint}?${queryString}` : endPoint;
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const busFee = data?.data?.data ?? [];
          if (!Array.isArray(busFee) || busFee.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const manageBusFee of busFee) {
            if (manageBusFee.country_name)
              bucket.push(manageBusFee.country_name);
            if (manageBusFee.child_type) bucket.push(manageBusFee.child_type);
            if (manageBusFee.service_type);
            bucket.push(manageBusFee.service_type);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            configurationSlice.util.updateQueryData("getData", arg, (draft) => {
              const arr = draft?.data?.data ?? [];
              arr.forEach((manageBusFee) => {
                manageBusFee.country_name = pick(manageBusFee.country_name);
                manageBusFee.child_type = pick(manageBusFee.child_type);
                manageBusFee.service_type = pick(manageBusFee.service_type);
              });
            }),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["configurations"],
    }),
    // GET ALL STUDENT FEE API FOR (PARENT)
    getBusFee: builder.query({
      query: () => ({
        url: "bus-fee/get-bus-fee",
        method: "POST",
      }),
    }),
    // GET ALL STUDENT FEE API FOR (SUPER ADMIN | SCHOOL | VENDOR)
    getBusFeeCofig: builder.query({
      query: ({ page, limit, search, role }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);
        if (role) params.append("role", role);

        const queryString = params.toString();
        return queryString ? `bus-fee/config?${queryString}` : "bus-fee/config";
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const busFee = data?.data?.data ?? [];
          if (!Array.isArray(busFee) || busFee.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const manageBusFee of busFee) {
            if (manageBusFee.country_name)
              bucket.push(manageBusFee.country_name);
            if (manageBusFee.child_type) bucket.push(manageBusFee.child_type);
            if (manageBusFee.service_type);
            bucket.push(manageBusFee.service_type);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            configurationSlice.util.updateQueryData("getData", arg, (draft) => {
              const arr = draft?.data?.data ?? [];
              arr.forEach((manageBusFee) => {
                manageBusFee.country_name = pick(manageBusFee.country_name);
                manageBusFee.child_type = pick(manageBusFee.child_type);
                manageBusFee.service_type = pick(manageBusFee.service_type);
              });
            }),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["configurations"],
    }),

    // Get All Vehicle Years
    getVehicleYear: builder.query({
      query: ({ page, limit, search }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);

        const queryString = params.toString();
        return queryString ? `vehicle-year?${queryString}` : "vehicle-year";
      },
      providesTags: ["configurations"],
    }),

    // Get All Vehicle Make
    getVehicleMake: builder.query({
      query: ({ page, limit, search, endPoint }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);

        return `/vehicle-make?${params.toString()}`;
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const vehicle = data?.data?.makes ?? [];
          if (!Array.isArray(vehicle) || vehicle.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const vehicleMake of vehicle) {
            if (vehicleMake.name) bucket.push(vehicleMake.name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            configurationSlice.util.updateQueryData(
              "getVehicleMake",
              arg,
              (draft) => {
                const arr = draft?.data?.makes ?? [];
                arr.forEach((vehiclemake) => {
                  vehiclemake.name = pick(vehiclemake.name);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["configurations"],
    }),

    // Get All Vehicle Model
    getVehicleModel: builder.query({
      query: ({ page, limit, search, id }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);
        if (id) params.append("make_id", id);

        return `/vehicle-model?${params.toString()}`;
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const model = data?.data?.models ?? [];
          if (!Array.isArray(model) || model.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const vehicleModel of model) {
            if (vehicleModel.name) bucket.push(vehicleModel.name);
            if (vehicleModel.make_name) bucket.push(vehicleModel.make_name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            configurationSlice.util.updateQueryData(
              "getVehicleModel",
              arg,
              (draft) => {
                const arr = draft?.data?.models ?? [];
                arr.forEach((vehiclemodel) => {
                  vehiclemodel.name = pick(vehiclemodel.name);
                  vehiclemodel.make_name = pick(vehiclemodel.make_name);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["configurations"],
    }),

    // Get All Vehicle Color
    getVehicleColor: builder.query({
      query: ({ page, limit, search }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);

        return `/vehicle-color?${params.toString()}`;
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const color = data?.data?.colors ?? [];
          if (!Array.isArray(color) || color.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const vehicleColor of color) {
            if (vehicleColor.name) bucket.push(vehicleColor.name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            configurationSlice.util.updateQueryData(
              "getVehicleColor",
              arg,
              (draft) => {
                const arr = draft?.data?.colors ?? [];
                arr.forEach((vehicleColor) => {
                  vehicleColor.name = pick(vehicleColor.name);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["configurations"],
    }),

    // Get All Vehicle Type
    getVehicleType: builder.query({
      query: ({ page, limit, search }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (search) params.append("search", search);

        return `/vehicle-type?${params.toString()}`;
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const type = data?.data?.types ?? [];
          if (!Array.isArray(type) || type.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const vehicleType of type) {
            if (vehicleType.name) bucket.push(vehicleType.name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            configurationSlice.util.updateQueryData(
              "getVehicleType",
              arg,
              (draft) => {
                const arr = draft?.data?.types ?? [];
                arr.forEach((vehicleType) => {
                  vehicleType.name = pick(vehicleType.name);
                });
              },
            ),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["configurations"],
    }),

    // GET Record Details By ID
    getDetails: builder.query({
      query: ({ id, endPoint }) => `${endPoint}/${id}`,
      providesTags: ["configurations"],
    }),
    // GET Country States List
    getCountryStates: builder.query({
      query: (id) => `state?country=${id}`,
      providesTags: ["configurations"],
    }),
    // Create Record
    create: builder.mutation({
      query: ({ data, endPoint }) => ({
        url: endPoint,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["configurations"],
    }),

    // UPDATE Record
    update: builder.mutation({
      query: ({ data, endPoint }) => ({
        url: `${endPoint}/${data.id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["configurations"],
    }),

    // CHANGE Record STATUS
    changeStatus: builder.mutation({
      query: (data) => ({
        url: `bus-fee/config/${data.id}/active`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["configurations"],
    }),

    // DELETE Single Record
    deleteRecord: builder.mutation({
      query: ({ id, endPoint }) => ({
        url: `${endPoint}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["configurations"],
    }),

    // Create Record
    sendMessage: builder.mutation({
      query: (data) => ({
        url: "support",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetBusFeeCofigQuery, // NEW use
  useGetBusFeeQuery, // NEW use
  useGetDataQuery, // this not use in future
  useGetVehicleYearQuery, // NEW use
  useGetVehicleMakeQuery, // old use
  useGetVehicleModelQuery, // old use
  useGetVehicleColorQuery, // old use
  useGetVehicleTypeQuery, // old use
  useGetCountryStatesQuery,
  useCreateMutation, // old use
  useGetDetailsQuery,
  useUpdateMutation, // old use
  useDeleteRecordMutation,
  useSendMessageMutation,
  useChangeStatusMutation, // new use
} = configurationSlice;
